import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUp, Calendar, Clock, ChevronDown, List } from 'lucide-react';
import SEOHead from '../../components/SEOHead';
import BlogNotFound from '../../components/BlogNotFound';
import { getBlogBySlug } from '../../data/blogs';

const AIAgents = () => {
  const [activeSection, setActiveSection] = useState('');
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [tocOpen, setTocOpen] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);
  const blog = getBlogBySlug('ai-agents-peak-performance');

  useEffect(() => {
    setIsDesktop(window.innerWidth >= 1100);

    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 500);

      const sections = document.querySelectorAll('h2[id]');
      let current = '';

      sections.forEach(section => {
        const sectionTop = section.getBoundingClientRect().top;
        if (sectionTop <= 150) {
          current = section.getAttribute('id') || '';
        }
      });

      setActiveSection(current);
    };

    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 1100);
    };

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const yOffset = -120;
      const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
      setTocOpen(false);
    }
  };

  const tableOfContents = [
    { id: 'context', title: '1. Context' },
    { id: 'problem', title: '2. Problem Statement' },
    { id: 'framework', title: '3. Core Framework' },
    { id: 'comparison', title: '4. Comparative Analysis' },
    { id: 'use-cases', title: '5. Business Use Cases' },
    { id: 'benefits', title: '6. Benefits & Outcomes' },
    { id: 'challenges', title: '7. Challenges & Realities' },
    { id: 'future', title: '8. Future Outlook' },
    { id: 'conclusion', title: '9. Conclusion' },
    { id: 'faq', title: '10. Frequently Asked Questions' }
  ];

  const faqData = [
    {
      question: 'What is the key difference between AI Automation and AI Agents?',
      answer: 'AI Automation follows predefined rules and linear execution paths to complete specific tasks, while AI Agents are goal-oriented systems that can perceive their environment, make autonomous decisions, and adapt their approach based on changing circumstances. Agents can problem-solve in dynamic environments, whereas automation excels at executing repetitive, well-defined processes.'
    },
    {
      question: 'Can AI Agents really improve business outcomes? What kind of ROI should I expect?',
      answer: 'Yes, AI Agents deliver significant ROI. Businesses typically see 20-40% reduction in operational overhead, 30% increase in operational efficiency, and 25% improvement in customer satisfaction. For example, in customer service, agents can boost retention by 15%, while in supply chain management, they can decrease disruptions by 20% and lower operational costs by 10%.'
    },
    {
      question: 'What are the main limitations of AI Agents that I should be aware of?',
      answer: 'AI Agents face several challenges: context management difficulties in long-term tasks, shallow reasoning depth for complex multi-step logic, potential unpredictability making them unsuitable for mission-critical applications, high data and compute requirements, and ethical concerns around bias and decision transparency. These require careful design, monitoring, and robust safety frameworks.'
    },
    {
      question: 'How do AI Agents learn and improve over time?',
      answer: 'AI Agents learn through continuous feedback loops. They observe their environment, take actions toward goals, evaluate success, and learn from outcomes through Machine Learning and Deep Learning techniques. This iterative process allows them to refine decision-making, adapt strategies, and improve performance without requiring manual reprogramming.'
    },
    {
      question: 'Are AI Agents suitable for small businesses or only enterprises?',
      answer: 'While AI Agents traditionally required significant resources, democratization is happening rapidly. Over the next 12 months, advances in frameworks and platforms will make it easier for non-developers to configure specialized agents. Small businesses can start with targeted use cases like customer service or marketing personalization, then scale as they see ROI.'
    },
    {
      question: 'What industries benefit most from AI Agents?',
      answer: 'AI Agents excel in industries requiring complex problem-solving: Customer Service (personalized support), Manufacturing & Supply Chain (dynamic optimization), Marketing & Sales (adaptive campaigns), Finance & Compliance (fraud detection, regulatory navigation), Healthcare (diagnostic support), and Legal (contract analysis). Any industry facing ambiguous, dynamic challenges can benefit.'
    },
    {
      question: 'How do I ensure AI Agents make ethical and unbiased decisions?',
      answer: 'Implement robust governance frameworks: use diverse, high-quality training data, continuously monitor for bias, ensure transparency in decision-making (explainable AI), establish human oversight for critical decisions, conduct regular audits, and follow emerging ethical AI standards. Building trust requires making agent reasoning interpretable and accountable.'
    },
    {
      question: 'What will AI Agents look like in the next 12 months?',
      answer: 'Expect multi-agent systems where agents collaborate on complex problems, enhanced learning with less human intervention, democratized development through no-code platforms, stronger ethical frameworks with built-in explainability, seamless human-agent collaboration interfaces, and specialized vertical agents with deep domain expertise in healthcare, finance, and legal sectors.'
    }
  ];

  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "BlogPosting",
        "headline": blog?.title,
        "description": blog?.excerpt,
        "image": blog?.featuredImage,
        "author": {
          "@type": "Person",
          "name": blog?.author
        },
        "publisher": {
          "@type": "Organization",
          "name": "Agility",
          "logo": {
            "@type": "ImageObject",
            "url": "https://www.agilitytech.ai/agility hex fonts.png"
          }
        },
        "datePublished": blog?.publishDate,
        "dateModified": blog?.publishDate,
        "mainEntityOfPage": {
          "@type": "WebPage",
          "@id": `https://www.agilitytech.ai/insights/${blog?.slug}`
        },
        "keywords": blog?.seoKeywords.join(', '),
        "articleBody": "AI Agents represent the next frontier of intelligent automation, offering autonomous decision-making, continuous learning, and adaptive problem-solving capabilities beyond traditional rule-based automation..."
      },
      {
        "@type": "FAQPage",
        "mainEntity": faqData.map(faq => ({
          "@type": "Question",
          "name": faq.question,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": faq.answer
          }
        }))
      }
    ]
  };

  if (!blog) return <BlogNotFound />;

  return (
    <div className="min-h-screen" style={{ background: '#050505' }}>
      <SEOHead
        title={`${blog?.title} | Agility Blog`}
        description={blog?.excerpt}
        keywords={blog?.seoKeywords.join(', ')}
        url={`https://www.agilitytech.ai/insights/${blog?.slug}`}
        image={blog?.featuredImage}
        type="article"
        article={true}
        author={blog?.author}
        publishedTime={new Date(blog?.publishDate || '').toISOString()}
        modifiedTime={new Date(blog?.publishDate || '').toISOString()}
        structuredData={structuredData}
      />

      {/* Blog Hero Section */}
      <section className="pt-32 pb-16" style={{ background: 'linear-gradient(to bottom, #0B0B0B, #050505)' }}>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" style={{ maxWidth: '1200px' }}>
          <div style={{ maxWidth: '800px', margin: '0 auto' }}>
            {/* Category Tag */}
            <div className="mb-6">
              <span style={{
                display: 'inline-block',
                padding: '0.5rem 1rem',
                background: 'rgba(20, 184, 166, 0.15)',
                color: '#14b8a6',
                borderRadius: '9999px',
                fontSize: '0.875rem',
                fontWeight: '500',
                border: '1px solid rgba(20, 184, 166, 0.3)'
              }}>
                {blog.category}
              </span>
            </div>

            {/* Title */}
            <h1 style={{
              fontSize: 'clamp(2rem, 5vw, 3rem)',
              fontFamily: 'Sora, sans-serif',
              fontWeight: '700',
              color: '#ffffff',
              marginBottom: '1.5rem',
              lineHeight: '1.2',
              letterSpacing: '-0.02em'
            }}>
              {blog.title}
            </h1>

            {/* Meta Info */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1.5rem', color: '#9ca3af', marginBottom: '2rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Calendar size={16} />
                <span style={{ fontSize: '0.875rem' }}>{new Date(blog.publishDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Clock size={16} />
                <span style={{ fontSize: '0.875rem' }}>{blog.readTime} read</span>
              </div>
              <div style={{ fontSize: '0.875rem' }}>
                By <span style={{ color: '#14b8a6', fontWeight: '500' }}>{blog.author}</span>
              </div>
            </div>

            {/* Excerpt */}
            <p style={{
              fontSize: 'clamp(1.125rem, 2.5vw, 1.25rem)',
              lineHeight: '1.7',
              color: '#d1d5db',
              marginBottom: '2rem',
              fontFamily: 'Inter, sans-serif'
            }}>
              {blog.excerpt}
            </p>

            {/* Tags */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem' }}>
              {blog.tags.map((tag, index) => (
                <span
                  key={index}
                  style={{
                    padding: '0.375rem 0.875rem',
                    background: '#121212',
                    color: '#9ca3af',
                    borderRadius: '9999px',
                    fontSize: '0.875rem',
                    border: '1px solid #262626'
                  }}
                >
                  #{tag}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Main Content Section with Sticky TOC */}
      <section style={{ background: '#050505', paddingBottom: '5rem' }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          padding: '0 1.5rem',
          display: 'flex',
          minHeight: '100vh',
          gap: '3rem',
          flexDirection: isDesktop ? 'row' : 'column'
        }}>
          {/* Sticky TOC - Desktop */}
          {isDesktop && (
            <div style={{
              position: 'sticky',
              top: '100px',
              height: 'fit-content',
              width: '260px',
              flexShrink: 0,
              alignSelf: 'flex-start',
              zIndex: 999
            }}>
              <nav style={{
                background: '#121212',
                borderRadius: '12px',
                padding: '1.5rem',
                border: '1px solid #262626'
              }}>
                <h3 style={{
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  color: '#14b8a6',
                  marginBottom: '1rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  fontFamily: 'Sora, sans-serif'
                }}>
                  Table of Contents
                </h3>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  {tableOfContents.map((item) => (
                    <li key={item.id} style={{ marginBottom: '0.75rem' }}>
                      <button
                        onClick={() => scrollToSection(item.id)}
                        style={{
                          width: '100%',
                          textAlign: 'left',
                          padding: '0.5rem',
                          background: activeSection === item.id ? 'rgba(20, 184, 166, 0.1)' : 'transparent',
                          color: activeSection === item.id ? '#14b8a6' : '#9ca3af',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '0.875rem',
                          transition: 'all 0.2s',
                          fontFamily: 'Inter, sans-serif',
                          borderLeft: activeSection === item.id ? '2px solid #14b8a6' : '2px solid transparent',
                          paddingLeft: '0.75rem'
                        }}
                      >
                        {item.title}
                      </button>
                    </li>
                  ))}
                </ul>
              </nav>
            </div>
          )}

          {/* Mobile TOC */}
          {!isDesktop && (
            <div style={{ marginBottom: '2rem' }}>
              <button
                onClick={() => setTocOpen(!tocOpen)}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '1rem',
                  background: '#121212',
                  border: '1px solid #262626',
                  borderRadius: '12px',
                  color: '#14b8a6',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  fontFamily: 'Sora, sans-serif'
                }}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <List size={18} />
                  TABLE OF CONTENTS
                </span>
                <ChevronDown
                  size={18}
                  style={{
                    transform: tocOpen ? 'rotate(180deg)' : 'rotate(0)',
                    transition: 'transform 0.3s'
                  }}
                />
              </button>
              {tocOpen && (
                <nav style={{
                  background: '#121212',
                  borderRadius: '0 0 12px 12px',
                  padding: '1rem',
                  border: '1px solid #262626',
                  borderTop: 'none',
                  marginTop: '-1px'
                }}>
                  <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                    {tableOfContents.map((item) => (
                      <li key={item.id} style={{ marginBottom: '0.75rem' }}>
                        <button
                          onClick={() => scrollToSection(item.id)}
                          style={{
                            width: '100%',
                            textAlign: 'left',
                            padding: '0.5rem',
                            background: 'transparent',
                            color: '#9ca3af',
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: '0.875rem',
                            fontFamily: 'Inter, sans-serif'
                          }}
                        >
                          {item.title}
                        </button>
                      </li>
                    ))}
                  </ul>
                </nav>
              )}
            </div>
          )}

          {/* Main Content */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <article style={{
              color: '#e5e7eb',
              fontFamily: 'Inter, sans-serif',
              fontSize: 'clamp(1rem, 2vw, 1.125rem)',
              lineHeight: '1.8'
            }}>
              {/* Context Section */}
              <h2 id="context" style={{
                color: '#14b8a6',
                fontSize: 'clamp(1.5rem, 4vw, 2rem)',
                fontFamily: 'Sora, sans-serif',
                fontWeight: '600',
                marginBottom: '1.5rem',
                marginTop: '2rem',
                scrollMarginTop: '120px'
              }}>
                1. Context: The Dawn of Agentic AI in Business
              </h2>
              <p style={{ marginBottom: '1.5rem', textAlign: 'justify' }}>
                In today's fast-evolving digital landscape, businesses are constantly seeking ways to enhance efficiency, drive innovation, and maintain a competitive edge. The buzz around <strong style={{ color: '#ffffff' }}>Artificial Intelligence (AI)</strong> is louder than ever, particularly concerning <strong style={{ color: '#ffffff' }}>workflow optimization</strong> and <strong style={{ color: '#ffffff' }}>business productivity</strong>.
              </p>
              <p style={{ marginBottom: '1.5rem', textAlign: 'justify' }}>
                While <strong style={{ color: '#ffffff' }}>AI Automation</strong> has been a game-changer for streamlining repetitive tasks, a new, more sophisticated paradigm is emerging: <strong style={{ color: '#ffffff' }}>AI Agents</strong>. This shift isn't just an upgrade; it's a fundamental reimagining of how AI can solve complex problems, adapting and learning in dynamic environments. We're moving beyond mere task execution to intelligent problem-solving, promising unprecedented levels of operational excellence.
              </p>

              {/* Problem Statement Section */}
              <h2 id="problem" style={{
                color: '#14b8a6',
                fontSize: 'clamp(1.5rem, 4vw, 2rem)',
                fontFamily: 'Sora, sans-serif',
                fontWeight: '600',
                marginBottom: '1.5rem',
                marginTop: '2rem',
                scrollMarginTop: '120px'
              }}>
                2. Problem Statement: The Bottlenecks of Traditional Automation
              </h2>
                <p style={{ marginBottom: '1.5rem', textAlign: 'justify' }}>
                  Despite its undeniable benefits, traditional <strong style={{ color: '#ffffff' }}>AI Automation</strong>, often relying on rule-based systems and <strong style={{ color: '#ffffff' }}>Robotic Process Automation (RPA)</strong>, faces significant limitations that can hinder true business agility and incur hidden costs. These limitations directly impact key performance indicators (KPIs) such as operational efficiency, error rates, and adaptability to market changes:
                </p>

                <ul style={{ listStyle: 'none', padding: 0, marginBottom: '2rem' }}>
                  {[
                    'Linear Execution Paths Only: Automation strictly follows predefined steps. This rigidity makes systems brittle, leading to failures or interruptions when encountering unexpected scenarios or deviations from the programmed path.',
                    'Rule-Based Decision Making: If a situation isn\'t explicitly covered by a pre-programmed rule, traditional automation systems often fail or produce irrelevant outputs. This limits their effectiveness in ambiguous or novel situations.',
                    'Static Knowledge and Capabilities: Once deployed, traditional automation systems do not evolve or improve independently. Any enhancement necessitates human reprogramming, leading to ongoing maintenance costs.',
                    'Limited Context Understanding: Automation processes data based on its programming but lacks a deep understanding of the broader context, meaning, or underlying purpose of its actions.'
                  ].map((item, idx) => (
                    <li key={idx} style={{
                      display: 'flex',
                      marginBottom: '1rem',
                      paddingLeft: '1.5rem',
                      position: 'relative'
                    }}>
                      <span style={{
                        position: 'absolute',
                        left: '0',
                        top: '0.6rem',
                        width: '6px',
                        height: '6px',
                        borderRadius: '50%',
                        background: '#14b8a6'
                      }} />
                      <span style={{ color: '#e5e7eb', textAlign: 'justify' }}>{item}</span>
                    </li>
                  ))}
                </ul>

                <p style={{ marginBottom: '1.5rem', textAlign: 'justify' }}>
                  These limitations translate into increased operational inefficiencies, higher error correction costs, and missed opportunities for proactive problem-solving, ultimately impacting the bottom line.
                </p>

              {/* Core Framework Section */}
              <h2 id="framework" style={{
                color: '#14b8a6',
                fontSize: 'clamp(1.5rem, 4vw, 2rem)',
                fontFamily: 'Sora, sans-serif',
                fontWeight: '600',
                marginBottom: '1.5rem',
                marginTop: '2rem',
                scrollMarginTop: '120px'
              }}>
                3. Core Framework: Understanding AI Automation and AI Agents
              </h2>
                <p style={{ marginBottom: '2rem', textAlign: 'justify' }}>
                  To truly grasp the transformative potential, it's crucial to understand the distinct frameworks of <strong style={{ color: '#ffffff' }}>AI Automation</strong> and <strong style={{ color: '#ffffff' }}>AI Agents</strong>.
                </p>

                <h3 style={{
                  fontSize: 'clamp(1.25rem, 3vw, 1.5rem)',
                  fontFamily: 'Sora, sans-serif',
                  fontWeight: '600',
                  color: '#ffffff',
                  marginBottom: '1rem',
                  marginTop: '2rem'
                }}>
                  AI Automation
                </h3>
                <p style={{ marginBottom: '1.5rem', textAlign: 'justify' }}>
                  <strong style={{ color: '#14b8a6' }}>Definition:</strong> AI Automation involves the use of AI technologies, such as <strong style={{ color: '#ffffff' }}>Machine Learning (ML)</strong> and <strong style={{ color: '#ffffff' }}>Natural Language Processing (NLP)</strong>, to execute predefined, rule-based tasks or processes without human intervention. Its primary goal is to replicate human actions for repetitive, high-volume operations.
                </p>
                <p style={{ marginBottom: '2rem', textAlign: 'justify' }}>
                  <strong style={{ color: '#14b8a6' }}>How it Works:</strong> Automation typically functions by following a strict sequence of commands or rules. It receives input, processes it according to established logic, and produces an output. Think of it as a highly efficient digital assembly line.
                </p>

                {/* IMAGE 1: AI Automation Workflow */}
                <div style={{ margin: '2.5rem 0', background: '#121212', borderRadius: '12px', padding: '1.5rem', border: '1px solid #262626' }}>
                  <p style={{ color: '#14b8a6', fontSize: '0.875rem', fontWeight: '600', marginBottom: '1rem', textTransform: 'uppercase', letterSpacing: '0.05em', fontFamily: 'Inter, sans-serif' }}>
                    AI Automation: Rule-Based Task Execution
                  </p>
                  <div style={{
                    width: '100%',
                    height: 'clamp(240px, 40vw, 320px)',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    background: `linear-gradient(135deg, rgba(20,184,166,0.08), rgba(0,150,136,0.03)), url('https://images.unsplash.com/photo-1485827404703-89b55fcc595e?auto=format&fit=crop&q=80')`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                  }} />
                  <p style={{ color: '#9ca3af', fontSize: '0.875rem', marginTop: '1rem', fontStyle: 'italic', fontFamily: 'Inter, sans-serif' }}>
                    Traditional automation follows linear execution paths with predefined rules, excelling at repetitive tasks but struggling with unexpected scenarios and dynamic environments.
                  </p>
                </div>

                <h3 style={{
                  fontSize: 'clamp(1.25rem, 3vw, 1.5rem)',
                  fontFamily: 'Sora, sans-serif',
                  fontWeight: '600',
                  color: '#ffffff',
                  marginBottom: '1rem',
                  marginTop: '2rem'
                }}>
                  AI Agents
                </h3>
                <p style={{ marginBottom: '1.5rem', textAlign: 'justify' }}>
                  <strong style={{ color: '#14b8a6' }}>Definition:</strong> AI Agents are intelligent systems designed to perceive their environment, make autonomous decisions, and take actions to achieve specific goals, often in dynamic and uncertain environments. They are problem-solvers rather than just task executors, leveraging advanced AI capabilities like <strong style={{ color: '#ffffff' }}>Deep Learning</strong> and complex reasoning.
                </p>

                <p style={{ marginBottom: '1rem', textAlign: 'justify' }}>
                  <strong style={{ color: '#14b8a6' }}>How it Works:</strong> AI Agents operate through a sophisticated iterative process:
                </p>

                <ol style={{ listStyle: 'none', padding: 0, marginBottom: '2rem', counterReset: 'agent-steps' }}>
                  {[
                    'Start with a Goal: Instead of a procedure, an agent begins with a desired outcome.',
                    'Observe the Environment: Agents actively gather relevant information from their surroundings.',
                    'Plan the Approach: They strategize and adapt their plan based on the current situation, considering multiple possible routes.',
                    'Make Smart Decisions: At key junctures, agents decide whether to act, gather more information, or adjust tactics.',
                    'Evaluate Success: After taking action, the agent assesses the outcome against its goal.',
                    'Learn from Experience: Through feedback loops, agents continuously improve their understanding and decision-making for future tasks.'
                  ].map((step, idx) => (
                    <li key={idx} style={{
                      display: 'flex',
                      marginBottom: '1rem',
                      paddingLeft: '2.5rem',
                      position: 'relative',
                      counterIncrement: 'agent-steps'
                    }}>
                      <span style={{
                        position: 'absolute',
                        left: '0',
                        top: '0',
                        width: '28px',
                        height: '28px',
                        borderRadius: '50%',
                        background: '#14b8a6',
                        color: '#ffffff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '0.875rem',
                        fontWeight: '600'
                      }}>
                        {idx + 1}
                      </span>
                      <span style={{ color: '#e5e7eb', textAlign: 'justify' }}>{step}</span>
                    </li>
                  ))}
                </ol>

              {/* IMAGE 2: Comparison Visualization */}
              <div style={{ margin: '3rem 0', background: '#121212', borderRadius: '12px', padding: '1.5rem', border: '1px solid #262626' }}>
                <p style={{ color: '#14b8a6', fontSize: '0.875rem', fontWeight: '600', marginBottom: '1rem', textTransform: 'uppercase', letterSpacing: '0.05em', fontFamily: 'Inter, sans-serif' }}>
                  Automation vs Agents: A Visual Comparison
                </p>
                <div style={{
                  width: '100%',
                  height: 'clamp(240px, 40vw, 320px)',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  background: `linear-gradient(135deg, rgba(20,184,166,0.08), rgba(0,150,136,0.03)), url('https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&q=80')`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center'
                }} />
                <p style={{ color: '#9ca3af', fontSize: '0.875rem', marginTop: '1rem', fontStyle: 'italic', fontFamily: 'Inter, sans-serif' }}>
                  The table below contrasts traditional automation's rigid execution with AI Agents' adaptive, goal-oriented approach across key technical and business dimensions.
                </p>
              </div>

              {/* Comparative Analysis Section */}
              <h2 id="comparison" style={{
                color: '#14b8a6',
                fontSize: 'clamp(1.5rem, 4vw, 2rem)',
                fontFamily: 'Sora, sans-serif',
                fontWeight: '600',
                marginBottom: '1.5rem',
                marginTop: '2rem',
                scrollMarginTop: '120px'
              }}>
                4. Comparative Analysis: Automation vs. Agents
              </h2>

                <div style={{ overflowX: 'auto', background: '#121212', borderRadius: '12px', padding: '1.5rem', border: '1px solid #262626' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9375rem' }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid #262626' }}>
                        <th style={{ padding: '1rem', textAlign: 'left', color: '#14b8a6', fontFamily: 'Sora, sans-serif', fontWeight: '600' }}>Feature</th>
                        <th style={{ padding: '1rem', textAlign: 'left', color: '#14b8a6', fontFamily: 'Sora, sans-serif', fontWeight: '600' }}>AI Automation</th>
                        <th style={{ padding: '1rem', textAlign: 'left', color: '#14b8a6', fontFamily: 'Sora, sans-serif', fontWeight: '600' }}>AI Agents</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        { feature: 'Execution Path', automation: 'Linear, rigid, predefined', agents: 'Adaptive, dynamic, goal-oriented' },
                        { feature: 'Decision Making', automation: 'Rule-based, explicit programming', agents: 'Autonomous, context-aware, learns from experience' },
                        { feature: 'Learning', automation: 'Static, requires human updates', agents: 'Continuous, self-improving through feedback loops' },
                        { feature: 'Adaptability', automation: 'Low, brittle to unexpected situations', agents: 'High, adjusts to novel and complex scenarios' },
                        { feature: 'Context Understanding', automation: 'Limited, processes data superficially', agents: 'Deep, understands "why" and "how" within broader context' },
                        { feature: 'Primary Focus', automation: 'Task execution', agents: 'Problem-solving' },
                        { feature: 'Complexity Handled', automation: 'Simple, repetitive, predictable tasks', agents: 'Complex, dynamic, ambiguous challenges' },
                        { feature: 'Error Handling', automation: 'Fails or stops on undefined errors', agents: 'Can attempt recovery, learn from errors' }
                      ].map((row, idx) => (
                        <tr key={idx} style={{ borderBottom: '1px solid #262626', transition: 'background 0.2s' }}>
                          <td style={{ padding: '1rem', color: '#ffffff', fontWeight: '600' }}>{row.feature}</td>
                          <td style={{ padding: '1rem', color: '#d1d5db' }}>{row.automation}</td>
                          <td style={{ padding: '1rem', color: '#14b8a6', fontWeight: '500' }}>{row.agents}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

              {/* IMAGE 3: Business Success */}
              <div style={{ margin: '3rem 0', background: '#121212', borderRadius: '12px', padding: '1.5rem', border: '1px solid #262626' }}>
                <p style={{ color: '#14b8a6', fontSize: '0.875rem', fontWeight: '600', marginBottom: '1rem', textTransform: 'uppercase', letterSpacing: '0.05em', fontFamily: 'Inter, sans-serif' }}>
                  Business Transformation: Real-World AI Agent Impact
                </p>
                <div style={{
                  width: '100%',
                  height: 'clamp(240px, 40vw, 320px)',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  background: `linear-gradient(135deg, rgba(20,184,166,0.08), rgba(0,150,136,0.03)), url('https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&q=80')`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center'
                }} />
                <p style={{ color: '#9ca3af', fontSize: '0.875rem', marginTop: '1rem', fontStyle: 'italic', fontFamily: 'Inter, sans-serif' }}>
                  AI Agents deliver measurable ROI across customer service, supply chain, marketing, and finance transforming operations from reactive task execution to proactive problem-solving.
                </p>
              </div>

              {/* Business Use Cases Section */}
              <h2 id="use-cases" style={{
                color: '#14b8a6',
                fontSize: 'clamp(1.5rem, 4vw, 2rem)',
                fontFamily: 'Sora, sans-serif',
                fontWeight: '600',
                marginBottom: '1.5rem',
                marginTop: '2rem',
                scrollMarginTop: '120px'
              }}>
                5. Business Use Cases: From Efficiency to Strategic Advantage
              </h2>

                {/* Use Case Cards */}
                <div style={{ display: 'grid', gap: '1.5rem', marginTop: '2rem' }}>
                  {/* Customer Service */}
                  <div style={{
                    background: '#121212',
                    padding: '2rem',
                    borderRadius: '12px',
                    border: '1px solid #262626',
                    transition: 'all 0.3s'
                  }}>
                    <h3 style={{
                      fontSize: 'clamp(1.125rem, 2.5vw, 1.375rem)',
                      fontFamily: 'Sora, sans-serif',
                      fontWeight: '600',
                      color: '#ffffff',
                      marginBottom: '1.5rem'
                    }}>
                      Customer Service
                    </h3>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
                      <div>
                        <h4 style={{ color: '#14b8a6', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.75rem', textTransform: 'uppercase' }}>Automation Use Case</h4>
                        <p style={{ color: '#d1d5db', fontSize: '0.9375rem', marginBottom: '0.75rem' }}>
                          Handling high volumes of repetitive inquiries (e.g., "What's my order status?").
                        </p>
                        <p style={{ color: '#14b8a6', fontSize: '0.875rem', fontWeight: '600' }}>
                          Value: 30% faster response times, 20% reduction in agent workload
                        </p>
                      </div>
                      <div>
                        <h4 style={{ color: '#14b8a6', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.75rem', textTransform: 'uppercase' }}>Agent Use Case</h4>
                        <p style={{ color: '#d1d5db', fontSize: '0.9375rem', marginBottom: '0.75rem' }}>
                          Resolving complex issues requiring multi-channel data synthesis and personalized solutions.
                        </p>
                        <p style={{ color: '#14b8a6', fontSize: '0.875rem', fontWeight: '600' }}>
                          Value: 25% boost in customer satisfaction, 15% increase in retention
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Manufacturing & Supply Chain */}
                  <div style={{
                    background: '#121212',
                    padding: '2rem',
                    borderRadius: '12px',
                    border: '1px solid #262626',
                    transition: 'all 0.3s'
                  }}>
                    <h3 style={{
                      fontSize: 'clamp(1.125rem, 2.5vw, 1.375rem)',
                      fontFamily: 'Sora, sans-serif',
                      fontWeight: '600',
                      color: '#ffffff',
                      marginBottom: '1.5rem'
                    }}>
                      Manufacturing & Supply Chain
                    </h3>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
                      <div>
                        <h4 style={{ color: '#14b8a6', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.75rem', textTransform: 'uppercase' }}>Automation Use Case</h4>
                        <p style={{ color: '#d1d5db', fontSize: '0.9375rem', marginBottom: '0.75rem' }}>
                          Automating routine data entry in inventory management or simple quality checks.
                        </p>
                        <p style={{ color: '#14b8a6', fontSize: '0.875rem', fontWeight: '600' }}>
                          Value: 40% error reduction, 50% faster record-keeping
                        </p>
                      </div>
                      <div>
                        <h4 style={{ color: '#14b8a6', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.75rem', textTransform: 'uppercase' }}>Agent Use Case</h4>
                        <p style={{ color: '#d1d5db', fontSize: '0.9375rem', marginBottom: '0.75rem' }}>
                          Optimizing dynamic supply chain by reacting to real-time disruptions and re-routing logistics.
                        </p>
                        <p style={{ color: '#14b8a6', fontSize: '0.875rem', fontWeight: '600' }}>
                          Value: 20% fewer disruptions, 10% lower operational costs
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Marketing & Sales */}
                  <div style={{
                    background: '#121212',
                    padding: '2rem',
                    borderRadius: '12px',
                    border: '1px solid #262626',
                    transition: 'all 0.3s'
                  }}>
                    <h3 style={{
                      fontSize: 'clamp(1.125rem, 2.5vw, 1.375rem)',
                      fontFamily: 'Sora, sans-serif',
                      fontWeight: '600',
                      color: '#ffffff',
                      marginBottom: '1.5rem'
                    }}>
                      Marketing & Sales
                    </h3>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
                      <div>
                        <h4 style={{ color: '#14b8a6', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.75rem', textTransform: 'uppercase' }}>Automation Use Case</h4>
                        <p style={{ color: '#d1d5db', fontSize: '0.9375rem', marginBottom: '0.75rem' }}>
                          Scheduling social media posts or sending pre-defined email campaigns.
                        </p>
                        <p style={{ color: '#14b8a6', fontSize: '0.875rem', fontWeight: '600' }}>
                          Value: 60% faster campaign deployment, consistent messaging
                        </p>
                      </div>
                      <div>
                        <h4 style={{ color: '#14b8a6', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.75rem', textTransform: 'uppercase' }}>Agent Use Case</h4>
                        <p style={{ color: '#d1d5db', fontSize: '0.9375rem', marginBottom: '0.75rem' }}>
                          Crafting personalized campaigns that adapt in real-time based on customer behavior and market trends.
                        </p>
                        <p style={{ color: '#14b8a6', fontSize: '0.875rem', fontWeight: '600' }}>
                          Value: 18% higher conversion rates, 12% better ROAS
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Finance & Compliance */}
                  <div style={{
                    background: '#121212',
                    padding: '2rem',
                    borderRadius: '12px',
                    border: '1px solid #262626',
                    transition: 'all 0.3s'
                  }}>
                    <h3 style={{
                      fontSize: 'clamp(1.125rem, 2.5vw, 1.375rem)',
                      fontFamily: 'Sora, sans-serif',
                      fontWeight: '600',
                      color: '#ffffff',
                      marginBottom: '1.5rem'
                    }}>
                      Finance & Compliance
                    </h3>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
                      <div>
                        <h4 style={{ color: '#14b8a6', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.75rem', textTransform: 'uppercase' }}>Automation Use Case</h4>
                        <p style={{ color: '#d1d5db', fontSize: '0.9375rem', marginBottom: '0.75rem' }}>
                          Automating invoice processing or generating standard compliance reports.
                        </p>
                        <p style={{ color: '#14b8a6', fontSize: '0.875rem', fontWeight: '600' }}>
                          Value: 70% faster invoice processing, 35% less manual auditing
                        </p>
                      </div>
                      <div>
                        <h4 style={{ color: '#14b8a6', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.75rem', textTransform: 'uppercase' }}>Agent Use Case</h4>
                        <p style={{ color: '#d1d5db', fontSize: '0.9375rem', marginBottom: '0.75rem' }}>
                          Proactively detecting complex fraud patterns and navigating intricate regulatory changes.
                        </p>
                        <p style={{ color: '#14b8a6', fontSize: '0.875rem', fontWeight: '600' }}>
                          Value: 25% fraud reduction, 99% regulatory compliance
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

              {/* Benefits & Outcomes Section */}
              <h2 id="benefits" style={{
                color: '#14b8a6',
                fontSize: 'clamp(1.5rem, 4vw, 2rem)',
                fontFamily: 'Sora, sans-serif',
                fontWeight: '600',
                marginBottom: '1.5rem',
                marginTop: '2rem',
                scrollMarginTop: '120px'
              }}>
                6. Benefits & Outcomes: The Tangible Impact
              </h2>
                <p style={{ marginBottom: '2rem', textAlign: 'justify' }}>
                  The transition to AI Agents offers a myriad of benefits, both technical and business-oriented, leading to significant improvements across various KPIs.
                </p>

                <h3 style={{
                  fontSize: 'clamp(1.25rem, 3vw, 1.5rem)',
                  fontFamily: 'Sora, sans-serif',
                  fontWeight: '600',
                  color: '#ffffff',
                  marginBottom: '1rem',
                  marginTop: '2rem'
                }}>
                  Technical Outcomes
                </h3>
                <ul style={{ listStyle: 'none', padding: 0, marginBottom: '2rem' }}>
                  {[
                    'Enhanced System Adaptability: Systems become more flexible, capable of handling unforeseen situations without human intervention.',
                    'Improved Data Utilization: Agents actively seek and integrate relevant information, leading to more comprehensive and intelligent data processing.',
                    'Increased System Resilience: With built-in error handling and learning mechanisms, agents can recover from issues more gracefully, reducing downtime.',
                    'Scalability for Complexity: AI Agents can tackle problems that are too complex or dynamic for traditional automation.',
                    'Continuous Improvement: Through feedback loops and Machine Learning, agentic systems continually refine their performance.'
                  ].map((benefit, idx) => (
                    <li key={idx} style={{
                      display: 'flex',
                      marginBottom: '1rem',
                      paddingLeft: '1.5rem',
                      position: 'relative'
                    }}>
                      <span style={{
                        position: 'absolute',
                        left: '0',
                        top: '0.6rem',
                        width: '6px',
                        height: '6px',
                        borderRadius: '50%',
                        background: '#14b8a6'
                      }} />
                      <span style={{ color: '#e5e7eb', textAlign: 'justify' }}>{benefit}</span>
                    </li>
                  ))}
                </ul>

                <h3 style={{
                  fontSize: 'clamp(1.25rem, 3vw, 1.5rem)',
                  fontFamily: 'Sora, sans-serif',
                  fontWeight: '600',
                  color: '#ffffff',
                  marginBottom: '1rem',
                  marginTop: '2rem'
                }}>
                  Business Outcomes
                </h3>
                <ul style={{ listStyle: 'none', padding: 0, marginBottom: '2rem' }}>
                  {[
                    'Significant Cost Reduction: By automating complex problem-solving, businesses can see a 20-40% reduction in operational overhead and manual intervention.',
                    'Boosted Operational Efficiency: Streamlined workflows and autonomous decision-making can increase overall efficiency by 30% or more.',
                    'Accelerated Innovation: Freeing up human talent from routine tasks allows for greater focus on strategic initiatives and innovation.',
                    'Superior Customer Experience: Personalized and proactive service delivered by agents can lead to a 25% increase in customer satisfaction and loyalty.',
                    'Faster Time-to-Market: Automating complex development steps can reduce time-to-market for new products and services by 15-20%.',
                    'Data-Driven Strategic Insights: Agents can process vast amounts of data and identify patterns, offering deeper insights for strategic planning.'
                  ].map((benefit, idx) => (
                    <li key={idx} style={{
                      display: 'flex',
                      marginBottom: '1rem',
                      paddingLeft: '1.5rem',
                      position: 'relative'
                    }}>
                      <span style={{
                        position: 'absolute',
                        left: '0',
                        top: '0.6rem',
                        width: '6px',
                        height: '6px',
                        borderRadius: '50%',
                        background: '#14b8a6'
                      }} />
                      <span style={{ color: '#e5e7eb', textAlign: 'justify' }}>{benefit}</span>
                    </li>
                  ))}
                </ul>

              {/* Challenges & Realities Section */}
              <h2 id="challenges" style={{
                color: '#14b8a6',
                fontSize: 'clamp(1.5rem, 4vw, 2rem)',
                fontFamily: 'Sora, sans-serif',
                fontWeight: '600',
                marginBottom: '1.5rem',
                marginTop: '2rem',
                scrollMarginTop: '120px'
              }}>
                7. Challenges & Realities: Navigating the Implementation Journey
              </h2>
                <p style={{ marginBottom: '1.5rem', textAlign: 'justify' }}>
                  While the promise of AI Agents is immense, their implementation comes with its own set of complexities and realities that organizations must address:
                </p>

                <ul style={{ listStyle: 'none', padding: 0, marginBottom: '2rem' }}>
                  {[
                    'Implementation Complexity: Designing, training, and deploying sophisticated AI Agents requires specialized expertise in areas like Machine Learning, Deep Learning, and system architecture.',
                    'Data Quality and Availability: Agents are highly dependent on high-quality, relevant data for training and operation. Poor data can lead to biased or ineffective agent performance.',
                    'Ethical Considerations and Bias: Ensuring fairness, transparency, and accountability in agent decision-making is paramount. Addressing potential biases in training data is a significant challenge.',
                    'Security and Privacy: Agents often interact with sensitive data and systems, raising concerns about data privacy, intellectual property, and cybersecurity vulnerabilities.',
                    'Change Management: Integrating AI Agents into existing workflows requires significant organizational change, including upskilling employees and fostering human-AI collaboration.',
                    'Cost of Development and Maintenance: While offering long-term ROI, the initial investment in developing, testing, and maintaining advanced AI Agent systems can be substantial.',
                    'Explainability and Trust: Understanding how an AI Agent arrived at a particular decision (interpretability) is crucial for building trust and ensuring regulatory compliance.',
                    'Non-deterministic Behavior: The adaptive nature of agents can sometimes lead to non-deterministic outcomes, making debugging and predicting behavior more challenging.'
                  ].map((challenge, idx) => (
                    <li key={idx} style={{
                      display: 'flex',
                      marginBottom: '1rem',
                      paddingLeft: '1.5rem',
                      position: 'relative'
                    }}>
                      <span style={{
                        position: 'absolute',
                        left: '0',
                        top: '0.6rem',
                        width: '6px',
                        height: '6px',
                        borderRadius: '50%',
                        background: '#14b8a6'
                      }} />
                      <span style={{ color: '#e5e7eb', textAlign: 'justify' }}>{challenge}</span>
                    </li>
                  ))}
                </ul>

              {/* Future Outlook Section */}
              <h2 id="future" style={{
                color: '#14b8a6',
                fontSize: 'clamp(1.5rem, 4vw, 2rem)',
                fontFamily: 'Sora, sans-serif',
                fontWeight: '600',
                marginBottom: '1.5rem',
                marginTop: '2rem',
                scrollMarginTop: '120px'
              }}>
                8. Future Outlook: The Next 12 Months of Agentic AI
              </h2>
                <p style={{ marginBottom: '1.5rem', textAlign: 'justify' }}>
                  The next 12 months will witness an accelerated evolution of AI Agents, solidifying their role as indispensable assets for modern businesses. We anticipate several key trends:
                </p>

                <ul style={{ listStyle: 'none', padding: 0, marginBottom: '2rem' }}>
                  {[
                    'Rise of Multi-Agent Systems: More sophisticated systems will emerge where multiple AI Agents collaborate to solve even more complex, multi-faceted problems, mimicking human team dynamics.',
                    'Enhanced Learning and Adaptability: Agents will become even more adept at continuous learning from real-world interactions, requiring less human intervention for improvement.',
                    'Democratization of Agent Development: Advances in frameworks and platforms will make it easier for non-developers to configure and deploy specialized AI Agents.',
                    'Stronger Ethical AI Frameworks: Increased focus on responsible AI will lead to more robust governance, transparency, and explainability tools embedded within agentic systems.',
                    'Seamless Human-Agent Collaboration: Development of intuitive interfaces will facilitate more effective partnerships between human workers and AI Agents.',
                    'Specialized Vertical Agents: Proliferation of highly specialized agents tailored to specific industry verticals (e.g., healthcare, finance, legal), providing deep domain expertise.'
                  ].map((trend, idx) => (
                    <li key={idx} style={{
                      display: 'flex',
                      marginBottom: '1rem',
                      paddingLeft: '1.5rem',
                      position: 'relative'
                    }}>
                      <span style={{
                        position: 'absolute',
                        left: '0',
                        top: '0.6rem',
                        width: '6px',
                        height: '6px',
                        borderRadius: '50%',
                        background: '#14b8a6'
                      }} />
                      <span style={{ color: '#e5e7eb', textAlign: 'justify' }}>{trend}</span>
                    </li>
                  ))}
                </ul>

              {/* Conclusion Section */}
              <h2 id="conclusion" style={{
                color: '#14b8a6',
                fontSize: 'clamp(1.5rem, 4vw, 2rem)',
                fontFamily: 'Sora, sans-serif',
                fontWeight: '600',
                marginBottom: '1.5rem',
                marginTop: '2rem',
                scrollMarginTop: '120px'
              }}>
                9. Conclusion: Orchestrating a Smarter Future
              </h2>
                <p style={{ marginBottom: '1.5rem', textAlign: 'justify' }}>
                  Both <strong style={{ color: '#ffffff' }}>AI Automation</strong> and <strong style={{ color: '#ffffff' }}>AI Agents</strong> hold significant value in the modern enterprise. While traditional automation excels at executing defined tasks with precision and speed, AI Agents represent the next frontier, offering unparalleled capabilities in problem-solving, adaptation, and continuous learning.
                </p>
                <p style={{ marginBottom: '1.5rem', textAlign: 'justify' }}>
                  By moving beyond linear execution to goal-oriented intelligence, businesses can unlock new levels of efficiency, drive innovation, and gain a substantial competitive advantage. The choice isn't necessarily one or the other; rather, it's about strategically deploying the right AI solution for the right challenge, orchestrating a smarter, more productive future.
                </p>

              {/* FAQ Section */}
              <h2 id="faq" style={{
                color: '#14b8a6',
                fontSize: 'clamp(1.5rem, 4vw, 2rem)',
                fontFamily: 'Sora, sans-serif',
                fontWeight: '600',
                marginBottom: '1.5rem',
                marginTop: '2rem',
                scrollMarginTop: '120px'
              }}>
                10. Frequently Asked Questions
              </h2>

                <div style={{ display: 'grid', gap: '1.5rem', marginTop: '2rem' }}>
                  {faqData.map((faq, idx) => (
                    <div
                      key={idx}
                      style={{
                        background: '#121212',
                        padding: '1.75rem',
                        borderRadius: '12px',
                        border: '1px solid #262626',
                        transition: 'all 0.3s'
                      }}
                    >
                      <h3 style={{
                        fontSize: 'clamp(1.125rem, 2.5vw, 1.25rem)',
                        fontFamily: 'Sora, sans-serif',
                        fontWeight: '600',
                        color: '#ffffff',
                        marginBottom: '1rem'
                      }}>
                        {faq.question}
                      </h3>
                      <p style={{ color: '#d1d5db', lineHeight: '1.7', marginBottom: '0' }}>
                        {faq.answer}
                      </p>
                    </div>
                  ))}
                </div>

              {/* CTA Section */}
              <section style={{
                background: 'radial-gradient(circle at center, rgba(20, 184, 166, 0.15) 0%, rgba(20, 184, 166, 0.05) 50%, transparent 100%)',
                padding: '3rem',
                borderRadius: '16px',
                border: '1px solid rgba(20, 184, 166, 0.3)',
                textAlign: 'center',
                marginBottom: '4rem'
              }}>
                <h2 style={{
                  fontSize: 'clamp(1.5rem, 4vw, 2rem)',
                  fontFamily: 'Sora, sans-serif',
                  fontWeight: '700',
                  color: '#ffffff',
                  marginBottom: '1.5rem'
                }}>
                  Discover Your AI Advantage
                </h2>
                <p style={{
                  fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                  color: '#d1d5db',
                  marginBottom: '2rem',
                  maxWidth: '700px',
                  margin: '0 auto 2rem'
                }}>
                  Ready to explore how AI Agents can transform your business operations and propel you towards your strategic goals? Contact us today for a personalized consultation or to discuss a Proof of Concept (POC). Let's unlock the full potential of intelligent automation together.
                </p>
                <Link
                  to="/contact"
                  style={{
                    display: 'inline-block',
                    padding: '1rem 2.5rem',
                    background: '#14b8a6',
                    color: '#ffffff',
                    borderRadius: '8px',
                    textDecoration: 'none',
                    fontWeight: '600',
                    fontSize: '1rem',
                    fontFamily: 'Sora, sans-serif',
                    transition: 'all 0.3s',
                    border: 'none',
                    cursor: 'pointer'
                  }}
                >
                  Get Started Today
                </Link>
              </section>
            </article>
          </div>
        </div>
      </section>

      {/* Back to Top Button */}
      {showBackToTop && (
        <button
          onClick={scrollToTop}
          style={{
            position: 'fixed',
            bottom: '2rem',
            right: '2rem',
            width: '48px',
            height: '48px',
            borderRadius: '50%',
            background: '#14b8a6',
            border: 'none',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#ffffff',
            boxShadow: '0 4px 12px rgba(20, 184, 166, 0.3)',
            transition: 'all 0.3s',
            zIndex: 1000
          }}
          aria-label="Back to top"
        >
          <ArrowUp size={24} />
        </button>
      )}
    </div>
  );
};

export default AIAgents;
